/**
 * ONE CASE. UF-07, FR-U-DSP-002 and FR-U-DSP-005.
 *
 * "Track the status of a dispute to resolution", and "view the status of any compensation claim."
 *
 * ## What this screen shows, and what it never will
 *
 * It shows the RESOLUTION — what we decided, in words written for them.
 *
 * It never shows the officer's NOTES. Those are the investigation: what the officer concluded and
 * why, written for the next officer and for whoever reviews this in two years. They are a different
 * column in the database on purpose, and if the two were ever merged, either the investigation record
 * would become uselessly vague or the customer would receive an internal note about themselves.
 */

import { useConfirm } from '@/components/confirm';
import { useCallback, useEffect, useState } from 'react';
import { fmtDateTime } from '@/lib/format';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type Dispute } from '@/lib/api';
import { StatusBadge } from '@/components/dispute';
import { ErrorState, LoadingCard } from '@/components/states';

export default function Case() {
  const confirm = useConfirm();
  // React Router types a param as possibly absent; this route cannot match without one.
  const { caseId = '' } = useParams();

  const [item, setItem] = useState<Dispute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItem(await api.dispute(caseId));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this case.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingCard lines={4} />;
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!item) return null;

  const open = item.status !== 'resolved' && item.status !== 'withdrawn';

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div>
        <Link to="/support" className="text-sm text-brand hover:underline">
          ← Support
        </Link>

        <div className="mt-2 flex items-start justify-between gap-3">
          <h1 className="text-page-title text-ink">{item.subject}</h1>
          <StatusBadge status={item.status} />
        </div>

        <p className="mt-1 text-sm text-ink-muted">Reported {fmtDateTime(item.opened_at)}</p>
      </div>

      {/* THE PROMISE, and whether we are keeping it. */}
      {open ? (
        <section className="rounded-card border border-border bg-surface p-4">
          <p className="text-sm text-ink">
            We will get back to you by{' '}
            <strong className="font-medium">{fmtDateTime(item.sla_due_at)}</strong>.
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            That clock started the moment you reported this, not when somebody picked it up.
          </p>
        </section>
      ) : null}

      {item.status === 'resolved' && item.resolution ? (
        <section className="rounded-card border border-accent/30 bg-accent/5 p-5">
          <h2 className="text-sm font-medium text-accent">What we decided</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink">{item.resolution}</p>
        </section>
      ) : null}

      {/* THE COMPENSATION CLAIM. FR-U-DSP-005.
          `assessed` and `approved` mean NO MONEY HAS MOVED, and the participant is told so plainly:
          an officer saying we owe them money is not the same as being paid, and letting somebody
          believe otherwise for two days is its own small cruelty. */}
      {item.compensation ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">Compensation</h2>

          <p className="tabular mt-2 text-xl font-semibold text-ink">
            {item.compensation.asset} {money(item.compensation.amount)}
          </p>

          <p className="mt-2 text-sm text-ink-muted">
            {
              {
                assessed:
                  'An officer has assessed that we owe you this. A second officer has to agree before it is paid. No money has moved yet.',
                approved:
                  'Two officers have agreed. The payment is being made and will arrive in your wallet.',
                paid: 'Paid into your wallet.',
                rejected: 'We did not uphold this claim.',
              }[item.compensation.status]
            }
          </p>

          {item.compensation.paid_at ? (
            <p className="mt-1 text-xs text-ink-muted">
              Paid {fmtDateTime(item.compensation.paid_at)}
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="rounded-card border border-border bg-surface p-5">
        <h2 className="text-sm font-medium text-ink">What you told us</h2>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-muted">
          {item.detail}
        </p>

        {item.correlation_id ? (
          <Link
            to={`/activity/${item.correlation_id}`}
            className="mt-3 inline-block font-mono text-micro text-brand hover:underline"
          >
            About payment {item.correlation_id}
          </Link>
        ) : null}
      </section>

      {open ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="text-sm font-medium text-ink">Add evidence</h2>
          <p className="mt-1 text-sm text-ink-muted">
            A screenshot, a photograph, a receipt. Anything that helps us see what you saw.
          </p>

          <input
            type="file"
            accept="image/*,application/pdf"
            disabled={uploading}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              setUploading(true);
              setError(null);

              try {
                await api.attachDisputeEvidence(caseId, file);
                await load();
              } catch (err) {
                setError(
                  err instanceof LipaApiError ? err.detail.message : 'We could not attach that.',
                );
              } finally {
                setUploading(false);
              }
            }}
            className="mt-3 w-full rounded-lg border border-border px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-canvas file:px-3 file:py-1 file:text-xs"
          />

          <p className="mt-1 text-xs text-ink-muted">
            We fingerprint the file so it can be proved later that this is what you sent us.
          </p>
        </section>
      ) : null}

      {open ? (
        <button
          onClick={async () => {
            const ok = await confirm({
              title: 'Withdraw this case?',
              body: 'We will stop investigating it. You can always raise a new case.',
              confirmLabel: 'Withdraw case',
              danger: true,
            });
            if (!ok) return;
            await api.withdrawDispute(caseId);
            await load();
          }}
          className="w-full rounded-lg border border-border bg-surface py-2.5 text-sm font-medium text-ink-muted transition hover:bg-canvas"
        >
          Withdraw this case
        </button>
      ) : null}
    </div>
  );
}

/** Minor units to a display string. Exact, via BigInt: this is the money we owe somebody. */
function money(minor: string): string {
  const v = BigInt(minor);
  const whole = v / 100n;
  const cents = v % 100n;

  return `${whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents
    .toString()
    .padStart(2, '0')}`;
}
